import type { Membership, MembershipPlan, MembershipStatus } from './types';

/** Lifecycle of one remote resource as the screen sees it. */
export type LoadState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; data: T };

/** Waiting for a `pending` purchase: polling `/me`, or given up after a minute. */
export type MembershipActivationState = 'polling' | 'stalled';

/** Everything the screen state depends on. */
export interface MembershipViewInput {
  /** `/me`: `null` data means the customer has no membership (404). */
  me: LoadState<Membership | null>;
  /** Plans of `zoneId`; only consulted when `zoneId` is known. */
  plans: LoadState<MembershipPlan[]>;
  /** Whether the customer has selected a delivery address. */
  hasDeliveryAddress: boolean;
  /** An address is selected but its ETA (and so its zone) has not arrived yet. */
  isEtaPending: boolean;
  /** Courier zone of the selected address; `null` when unknown or absent. */
  zoneId: string | null;
  /** A `pending` purchase is being waited for; absent or `null` otherwise. */
  activation?: MembershipActivationState | null;
  /** The club is switched off on the backend (`MEMBERSHIPS_DISABLED`); absent or `false` otherwise. */
  isDisabled?: boolean;
  /** Current time in ms, for the age of a `pending_card`; `Date.now()` when absent. */
  now?: number;
}

/** Screen state of the membership page. */
export type MembershipView =
  | { kind: 'loading' }
  | { kind: 'error' }
  /** The club is switched off on the backend: nothing to offer and nothing to retry. */
  | { kind: 'unavailable' }
  | { kind: 'no-address' }
  | { kind: 'no-plans' }
  | { kind: 'offer'; plans: MembershipPlan[] }
  | {
      kind: 'activating';
      /** The minute ran out; the customer is asked to refresh. */
      stalled: boolean;
    }
  | {
      kind: 'member';
      membership: Membership;
      /**
       * The member's plan does not cover the courier zone of the selected
       * address, as the plan's `zoneIds` in `/me` say, whatever the source and
       * the delivery method — the cart leaves click & collect out on its own
       * (`cartZoneMismatch`). Never without a known zone or when `/me` names
       * no zones.
       */
      zoneMismatch: boolean;
      /** The zone check is settled: the ETA has arrived. */
      isZoneChecked: boolean;
    };

export type MembershipViewKind = MembershipView['kind'];

const NOT_A_MEMBERSHIP: ReadonlySet<MembershipStatus> = new Set([
  'pending_card',
  'expired',
]);

/** A purchase (`pending_card`) and an `expired` membership count as none. */
export const hasMembership = (
  membership: Membership | null,
): membership is Membership =>
  membership !== null && !NOT_A_MEMBERSHIP.has(membership.status);

/**
 * How long a `pending_card` counts as a purchase that may still be charging,
 * by its `created_at`: the backend `MEMBERSHIP_PENDING_CARD_TTL_MINUTES`
 * (30 minutes). The backend itself does not expire one at that moment — its
 * scheduler runs hourly (`MEMBERSHIPS_SCHEDULER_CRON_SCHEDULE`, `0 * * * *`)
 * and keeps a `pending_card` whose first payment is still settling (up to
 * `MEMBERSHIP_FIRST_PAYMENT_SETTLE_DAYS`, 7 days, once Adyen has a
 * `pspReference`, 60 minutes without one), so `/me` may show one long after.
 */
export const PENDING_CARD_TTL_MS = 30 * 60 * 1000;

/**
 * A `pending_card` younger than `PENDING_CARD_TTL_MS` by its `createdAt`,
 * whoever started it — this tab, another tab or another device: Join must not
 * offer a second payment for it while it may still be charging. Without a
 * readable `createdAt` (`/me` does not send one yet) it is not, and neither is
 * an older one: Join stays available, since the backend guards a second
 * charge itself — a new checkout (`resumeUnfinished`) returns the unfinished
 * one as `pending` while its money may be in flight and closes one that holds
 * none (an abandoned 3DS). A purchase of this customer is waited for through
 * its activation marker instead.
 */
export const isUnsettledPurchase = (
  membership: Membership | null,
  now: number,
): boolean => {
  if (membership?.status !== 'pending_card' || !membership.createdAt) {
    return false;
  }
  const createdAt = Date.parse(membership.createdAt);
  return !Number.isNaN(createdAt) && now - createdAt < PENDING_CARD_TTL_MS;
};

/** Whether `plan` covers `zoneId`; zone ids compare case-insensitively. A plan without `zoneIds` is taken as covering it. */
export const coversZone = (
  plan: MembershipPlan | undefined,
  zoneId: string,
): boolean => {
  const zoneIds = plan?.zoneIds;
  if (!Array.isArray(zoneIds)) {
    return true;
  }
  const wanted = zoneId.toLowerCase();
  return zoneIds.some(
    (id) => typeof id === 'string' && id.toLowerCase() === wanted,
  );
};

const toMemberView = (
  membership: Membership,
  zoneId: string | null,
  isEtaPending: boolean,
): MembershipView => ({
  kind: 'member',
  membership,
  zoneMismatch: zoneId !== null && !coversZone(membership.plan, zoneId),
  isZoneChecked: !isEtaPending,
});

/**
 * Resolves the screen state. A club switched off on the backend wins over
 * everything; then a membership, then a purchase being activated — a wait
 * for it, kept even while `/me` fails, or a `pending_card` that may still be
 * charging (`isUnsettledPurchase`; with no wait running it is shown stalled,
 * with "Refresh"); without either the address, then the ETA, then the
 * courier zone, then its plans decide.
 */
export const toMembershipView = (
  input: MembershipViewInput,
): MembershipView => {
  const {
    me,
    plans,
    hasDeliveryAddress,
    isEtaPending,
    zoneId,
    activation,
    isDisabled,
    now = Date.now(),
  } = input;

  if (isDisabled) {
    return { kind: 'unavailable' };
  }
  if (me.status === 'idle' || me.status === 'loading') {
    return { kind: 'loading' };
  }
  if (me.status === 'failed') {
    return activation
      ? { kind: 'activating', stalled: activation === 'stalled' }
      : { kind: 'error' };
  }
  if (hasMembership(me.data)) {
    return toMemberView(me.data, zoneId, isEtaPending);
  }
  if (activation) {
    return { kind: 'activating', stalled: activation === 'stalled' };
  }
  if (isUnsettledPurchase(me.data, now)) {
    return { kind: 'activating', stalled: true };
  }
  if (!hasDeliveryAddress) {
    return { kind: 'no-address' };
  }
  if (isEtaPending) {
    return { kind: 'loading' };
  }
  if (zoneId === null) {
    return { kind: 'no-plans' };
  }
  if (plans.status === 'idle' || plans.status === 'loading') {
    return { kind: 'loading' };
  }
  if (plans.status === 'failed') {
    return { kind: 'error' };
  }
  return plans.data.length > 0
    ? { kind: 'offer', plans: plans.data }
    : { kind: 'no-plans' };
};

/** Payload of `Membership: Page Viewed`. */
export interface MembershipPageViewedPayload {
  /** Screen state the customer saw. */
  state: Exclude<MembershipViewKind, 'loading'>;
  /** Membership status; `null` when there is no membership. */
  status: MembershipStatus | null;
  /** Code of the member's plan; `null` when there is no membership. */
  planCode: string | null;
  zoneMismatch: boolean;
}

/** Builds the Page Viewed payload, or `null` while the state is still loading or the member's zone is unchecked. */
export const toPageViewedPayload = (
  view: MembershipView,
): MembershipPageViewedPayload | null => {
  if (view.kind === 'loading') {
    return null;
  }
  if (view.kind === 'member') {
    if (!view.isZoneChecked) {
      return null;
    }
    return {
      state: 'member',
      status: view.membership.status,
      planCode: view.membership.plan.code,
      zoneMismatch: view.zoneMismatch,
    };
  }
  return {
    state: view.kind,
    status: null,
    planCode: null,
    zoneMismatch: false,
  };
};
